import React from "react";

const highlights = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    label: "Family-Friendly",
    desc: "Safe & cozy for all ages",
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
    label: "Top Rated in Salem",
    desc: "Trusted by thousands of families",
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    label: "Delivery & Takeaway",
    desc: "Enjoy favourites at home too",
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4" />
      </svg>
    ),
    label: "Special Occasions",
    desc: "Birthdays, dinners & more",
  },
];

const paragraphs = [
  "If you are searching for the best family restaurants in Salem, you may already know how hard it is to find the right one. Some places are too noisy, some don't have food for kids, and others are not clean or family-friendly. Going out to eat with your loved ones should be fun and stress-free — not a problem. At Barbequeen, we make family dining easy, happy, and full of delicious food.",
  "We understand that families want more than just good food. You want a safe and cozy place where everyone from children to grandparents feels welcome. That's why Barbequeen is proud to be one of the top family restaurants in Salem.",
  "Our menu is full of tasty options like grilled chicken, chicken biryani, crispy starters, veg and non-veg meals, fresh juices, and more. Whether you're coming for a birthday, a weekend dinner, or a simple night out, we have something for everyone.",
  "The seating is comfortable, the atmosphere is calm, and our staff is always friendly. You don't have to worry about the crowd or mess — just enjoy your time and your meal. We also offer takeaway and online delivery, so you can enjoy your favourite dishes even at home.",
  "When people look for the best restaurants in Salem for dinner with family, they choose Barbequeen because we care about food, service, and making your moments special.",
];

const FamilyRestaurantSection = () => {
  return (
    <section
      id="family-restaurant-salem"
      className="py-24 bg-white overflow-hidden"
      aria-label="Best Family Restaurants in Salem"
    >
      <div className="max-w-6xl mx-auto px-4 lg:px-8">

        {/* ── Section Header ── */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          <p className="text-[11px] font-bold tracking-[0.4em] uppercase text-[#9b1c2e] mb-3">
            Who We Are
          </p>

          {/* Separator */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="flex-1 max-w-[80px] h-px bg-gradient-to-r from-transparent to-[#9b1c2e]/40" />
            <svg className="w-5 h-5 text-[#9b1c2e] flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L9.5 8.5H3L8.3 12.7L6.2 19.5L12 15.8L17.8 19.5L15.7 12.7L21 8.5H14.5L12 2Z" />
            </svg>
            <div className="flex-1 max-w-[80px] h-px bg-gradient-to-l from-transparent to-[#9b1c2e]/40" />
          </div>

          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Best Family Restaurants in Salem
            <span className="block text-[#9b1c2e] mt-1 text-xl sm:text-2xl lg:text-3xl">
              A Perfect Place for Family Time
            </span>
          </h2>
          <div className="mt-5 w-16 h-0.5 bg-[#9b1c2e] mx-auto" />
        </div>

        {/* ── Highlight Badges ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-14">
          {highlights.map((item, i) => (
            <div
              key={i}
              className="flex flex-col items-center gap-3 p-5 bg-[#faf8f5] rounded-2xl border border-gray-100 hover:border-[#9b1c2e]/30 hover:shadow-md transition-all duration-300 group text-center"
            >
              <div className="w-12 h-12 rounded-xl bg-[#9b1c2e]/10 flex items-center justify-center text-[#9b1c2e] group-hover:bg-[#9b1c2e] group-hover:text-white transition-colors duration-300 flex-shrink-0">
                {item.icon}
              </div>
              <div>
                <p className="text-[12px] font-bold text-gray-900 uppercase tracking-wide leading-tight">
                  {item.label}
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Body Content ── */}
        <div className="max-w-4xl mx-auto space-y-5">
          {paragraphs.map((para, i) => (
            <p
              key={i}
              className="text-gray-600 text-[15px] leading-relaxed"
            >
              {para}
            </p>
          ))}

          {/* Closing emphasis */}
          <p className="text-gray-800 text-[15px] leading-relaxed font-medium border-l-4 border-[#9b1c2e] pl-4 py-1 bg-[#9b1c2e]/5 rounded-r-lg">
            Come visit us today and see why we are called one of the best family restaurants in Salem.
            At Barbequeen, great food brings families closer.
          </p>
        </div>

      </div>
    </section>
  );
};

export default FamilyRestaurantSection;

